import { cn } from "@tine/designsystem-utils";
import { Children, type ReactNode } from "react";

import useMediaQuery from "../../common/useMediaQuery";
import { mediaQueries } from "../../common/utils";

type TeaserListLayoutProps = {
   children: ReactNode;
   id: string;
};

type TeaserListLayoutRowProps = {
   nodes: ReactNode[];
};

const TeaserListLayoutRow = ({ nodes }: TeaserListLayoutRowProps) => {
   const columns: 1 | 2 | 3 | 4 = (nodes.length ? (nodes.length > 4 ? 4 : nodes.length) : 1) as 1 | 2 | 3 | 4;
   const classNames = cn("tw-mb-4 last:tw-mb-0", {
      "tw-flex tw-flex-row tw-gap-6 [&>*]:sm:tw-w-1/2": columns > 1,
      "[&>*]:md:tw-w-1/2": columns === 2,
      "[&>*]:md:tw-w-1/3": columns === 3,
      "[&>*]:md:tw-w-1/4": columns === 4
   });
   return <div className={classNames}>{nodes}</div>;
};

const rowLength = (count: number, isSm: boolean, isMd: boolean, isLg: boolean) => {
   if (!isSm) {
      return 1;
   }
   if (!isMd) {
      return 2;
   }
   if (!isLg) {
      return 3;
   }
   return count % 3 || !(count % 4) ? 4 : 3;
};

const TeaserListLayout = ({ children, id }: TeaserListLayoutProps) => {
   const isSm = useMediaQuery(mediaQueries.sm);
   const isMd = useMediaQuery(mediaQueries.md);
   const isLg = useMediaQuery(mediaQueries.lg);
   const nodes = Children.map(children, (child) => child);
   const count = nodes?.length || 0;
   const defaultRowLength = rowLength(count, isSm, isMd, isLg);

   let currentRow: ReactNode[] = [];
   const nodeRows: ReactNode[][] = [];

   // The following logic is to ensure the lovely pyramid layout where the fewest columns are on top
   if (defaultRowLength > 2) {
      for (const node of nodes?.reverse() || []) {
         if (currentRow.length === defaultRowLength) {
            nodeRows.unshift(currentRow);
            currentRow = [];
         }
         currentRow.unshift(node);
      }

      if (currentRow.length) {
         nodeRows.unshift(currentRow);
      }
   } else {
      for (const node of nodes || []) {
         if (currentRow.length === defaultRowLength) {
            nodeRows.push(currentRow);
            currentRow = [];
         }
         currentRow.push(node);
      }
      if (currentRow.length) {
         nodeRows.push(currentRow);
      }
   }

   if (defaultRowLength > 2 && nodeRows.length > 1 && nodeRows[0].length === 1) {
      nodeRows[0].push(nodeRows[1].shift());
   }

   return (
      <>
         {nodeRows.map((nodeRow, i) => (
            <TeaserListLayoutRow nodes={nodeRow} key={`teaser-list-layout-row-${id}-${i + 1}`} />
         ))}
      </>
   );
};

export default TeaserListLayout;
